// First-entry disclosure + informed-consent gate (spec §7 "Disclosure & ethics in-UI").
//
// One place for (a) every reader-facing consent string, (b) the browser-side consent
// record. Nothing about the stimulus is rendered and NO telemetry beyond the consent
// decision itself is collected until `readConsent()` returns a record (see App.tsx).
//
// Bump CONSENT_VERSION whenever the disclosure text materially changes: the stored
// record is version-keyed, so a bump re-prompts everyone who agreed to the old text.

// v1.1 — rewritten for the moderated, formative think-aloud study actually being run
// (doc/interview_protocol.md): one researcher-led session over one story, recorded and
// transcribed. Superseded v1.0, which described the planned unmoderated online panel
// experiment (k articles, random condition assignment, no recording).
export const CONSENT_VERSION = "1.1";

export interface StudyMeta {
  title: string;
  duration: string;
  support: string;
  /** Identity + contacts. EMPTY until the approved protocol is in hand — every one of
   *  these is optional at render time: an empty string drops its line (and the
   *  "Questions" section, if both contacts are empty) instead of showing an unfilled
   *  field. Fill them in and they appear; nothing else has to change. */
  org: string;
  pi: string;
  pi_email: string;
  irb_protocol: string;
  irb_contact: string;
}

/** Study identity + contacts shown on the consent page. */
export const STUDY: StudyMeta = {
  title: "Incident Lens: a research prototype for reading news coverage of police use of force",
  duration: "about an hour", // protocol §2 session guide totals ~55 min
  support: "988 Suicide & Crisis Lifeline, call or text 988 (US)",
  org: "",
  pi: "",
  pi_email: "",
  irb_protocol: "",
  irb_contact: "",
};

export interface ConsentRecord {
  version: string;
  agreed_at: string; // ISO timestamp of the agreement
  participant_id: string; // pseudonymous, generated here — never a name/email
}

const KEY = "incident_lens.consent";

// localStorage is unavailable in some privacy modes / sandboxes; degrade to an
// in-memory record so the gate still works for the length of the page session.
let memory: ConsentRecord | null = null;

function store(): Storage | null {
  try {
    const s = window.localStorage;
    const probe = "__il_probe__";
    s.setItem(probe, "1");
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

/** The stored consent for the CURRENT version, or null (first-time entry). */
export function readConsent(): ConsentRecord | null {
  const s = store();
  let rec: ConsentRecord | null = memory;
  if (s) {
    try {
      const raw = s.getItem(KEY);
      if (raw) rec = JSON.parse(raw) as ConsentRecord;
    } catch {
      rec = null;
    }
  }
  if (!rec || typeof rec !== "object" || !rec.participant_id) return null;
  // consent text changed since they agreed → treat as first-time, re-prompt
  if (rec.version !== CONSENT_VERSION) return null;
  return rec;
}

/** Record agreement to the current consent version; returns the stored record. */
export function writeConsent(): ConsentRecord {
  const rec: ConsentRecord = {
    version: CONSENT_VERSION,
    agreed_at: new Date().toISOString(),
    participant_id: "p_" + Math.random().toString(36).slice(2, 10),
  };
  memory = rec;
  try {
    store()?.setItem(KEY, JSON.stringify(rec));
  } catch {
    /* in-memory only */
  }
  return rec;
}

/** Withdrawal / decline: forget the agreement and the pseudonymous code. */
export function clearConsent(): void {
  memory = null;
  try {
    store()?.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

/** "March 14, 2019, 4:12 PM" — for the review-mode "you agreed on …" line. */
export function formatAgreedAt(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}
